import dayjs from "dayjs";

import { SelectOptions } from "../../assets/data/options";
import { Activity } from "../../backend/generated/activity-model";
import { ApproveUserInput } from "../../backend/generated/approve-user-input";
import { Company } from "../../backend/generated/company-model";
import { Experience } from "../../backend/generated/experience-model";
import { GetIdNowUserInput } from "../../backend/generated/get-id-now-user-input";
import { LoginUserInput } from "../../backend/generated/login-user-input";
import { RegisterCompanyInput } from "../../backend/generated/register-company-input";
import { RegisterUserInput } from "../../backend/generated/register-user-input";
import { SignDocumentUserInput } from "../../backend/generated/sign-document-user-input";
import { Skill } from "../../backend/generated/skill-model";
import { StartIdNowUserInput } from "../../backend/generated/start-id-now-user-input";
import { UpdateActivityInput } from "../../backend/generated/update-activity-input";
import { UpdateCompanyInput } from "../../backend/generated/update-company-input";
import { UpdateExperienceInput } from "../../backend/generated/update-experience-input";
import { UpdateSkillInput } from "../../backend/generated/update-skill-input";
import { UpdateUserInput } from "../../backend/generated/update-user-input";
import { User } from "../../backend/generated/user-model";
import { ActivityType, EducationLevel, Gender, IdNowStatus, IdTypes, InvestmentClass, ValidationMessages } from "../../backend/types/utils";
import { randomAdjective, randomAutocompleteOption, randomBoolean, randomDate, randomNoun, randomSelectOption, randomString } from "../../utils/random";
import { extract, isEmptyObject, prune } from "../../utils/utility";
import { CustomValidationArguments, validateBooleansTrue, validateCellphones, validateDates, validateNumbersInRange, validateRequiredNotDefault } from "../../utils/validation";

export enum AccountType {
  Private = 0,
  Company = 1,
}

export class UserManageable extends User {
  constructor(obj: any) {
    super();

    const defaultUserData = UserManageable.default();

    if (obj) {
      Object.assign(
        this,
        defaultUserData,
        prune(extract(
          obj,
          Object.keys(this)
        )),
        {
          company: obj.company ? {
            ...obj.company,
            legalEntityType: obj.company.legalEntityType ? obj.company.legalEntityType.toLowerCase() : defaultUserData.company.legalEntityType,
          } : defaultUserData.company,
          defaultPaymentMethod: obj.defaultPaymentMethod ? obj.defaultPaymentMethod : defaultUserData.defaultPaymentMethod,
          gender: obj.gender ? obj.gender.toLowerCase() as Gender : defaultUserData.gender,
          highestEducation: obj.highestEducation ? obj.highestEducation.toLowerCase() as EducationLevel : defaultUserData.highestEducation,
          idDocumentType: obj.idDocumentType ? obj.idDocumentType.toLowerCase() as IdTypes : defaultUserData.idDocumentType,
          idNowStatus: obj.idNowStatus ? obj.idNowStatus.toLowerCase() as IdNowStatus : defaultUserData.idNowStatus,
          experience: obj.experience ? {
            skills: obj.experience.skills ? obj.experience.skills.map((skill: Skill) => ({
              ...skill,
              type: skill.type.toLowerCase(),
              level: skill.level.toLowerCase(),
            })) : defaultUserData.experience.skills,
            activities: obj.experience.activities ? obj.experience.activities.map((activity: Activity) => ({
              ...activity,
              type: activity.type.toLowerCase(),
              levelWithoutAdvice: activity.levelWithoutAdvice.toLowerCase(),
              levelWithAdvice: activity.levelWithAdvice.toLowerCase(),
              levelWithAssetManagement: activity.levelWithAssetManagement.toLowerCase(),
              transactionsPerYear: activity.transactionsPerYear.toLowerCase(),
              volumePerYear: activity.volumePerYear.toLowerCase(),
            })) : defaultUserData.experience.activities,
          } : defaultUserData.experience,
        }
      );
    } else {
      Object.assign(
        this,
        defaultUserData
      );
    }

    const paymentMethods = [];
    if (this.paymentMethods) {
      for (let i = 0; i < this.paymentMethods.length; i++) {
        paymentMethods.push(this.paymentMethods[i]);
      }
    }
    this.paymentMethods = paymentMethods;

    const investments = [];
    if (this.investments) {
      for (let i = 0; i < this.investments.length; i++) {
        investments.push(this.investments[i]);
      }
    }
    this.investments = investments;
  }

  static default(): any {
    return {
      ...new User(),
      address: "",
      addressZipcode: "",
      addressCity: "",
      addressCountry: "",
      birthCity: "",
      birthDate: "",
      cellphone: "",
      citizenship: "",
      customerInfoEffectaGmbh: false,
      defaultPaymentMethod: "",
      employment: "",
      experience: {
        skills: [],
        activities: [],
      },
      firstName: "",
      gender: "",
      highestEducation: null,
      idDocumentCountry: "",
      idDocumentExpiryDate: "",
      idDocumentIssueDate: "",
      idDocumentIssuingAgency: "",
      idDocumentNumber: "",
      idDocumentType: "",
      notUSTax: false,
      politicallyExposed: "",
      politicallyExposedReason: "",
      privacyPolicy: false,
      taxNumber: "",
      termsEffectaGmbh: false,
      termsFinproAG: false,
      idNowStatus: null,
      company: {
        legalEntityType: "",
        registrationNumber: "",
        representativeFirstName: "",
        representativeLastName: "",
        tradeRegister: "",
        name: "",
      },
    }
  }

  static hasActivityExperience(
    input: any, type: ActivityType
  ) {
    const noExperience = SelectOptions().experience[0].value;
    const defaultExperience = "";
    const hasXp = (xp: any) => xp !== noExperience && xp !== defaultExperience;
    const experience = input.experience && input.experience.activities ? input.experience.activities.find((xp: Skill | Activity) => xp.type === type) : undefined;

    return (
      !input.notProvidingExperience &&
      experience !== undefined &&
      (hasXp(experience.levelWithoutAdvice) || hasXp(experience.levelWithAdvice) || hasXp(experience.levelWithAssetManagement))
    );
  }

  static isExperienceProvided(input: any) {
    return !!input.experience && !!input.experience.skills && input.experience.skills.length > 0 || !!input.experience && !!input.experience.activities && input.experience.activities.length > 0;
  }

  static isCompany(input: any): boolean {
    return !!input.company && !!input.company.name;
  }

  hasActivityExperience(type: ActivityType) {
    return UserManageable.hasActivityExperience(
      this,
      type
    );
  }

  isExperienceProvided() {
    return UserManageable.isExperienceProvided(this);
  }
}

export class ApproveUserManageable extends ApproveUserInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        ApproveUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        ApproveUserManageable.default()
      );
    }
  }

  static required(): any {
    return { };
  }

  static default(): any {
    return { ...new ApproveUserInput() };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    return validateRequiredNotDefault(
      {},
      ApproveUserManageable.required(),
      this,
      ApproveUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    data.gender = data.gender.toUpperCase();

    return data;
  }
}

export class GetIdNowUserManageable extends GetIdNowUserInput {
  constructor(obj?: any) {
    super();

    if (obj) {
      obj && Object.assign(
        this,
        GetIdNowUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        GetIdNowUserManageable.default()
      );
    }
  }

  static required(): any {
    return { idNowTransactionId: true };
  }

  static default(): any {
    return { ...new GetIdNowUserInput() };
  }

  serialize(): any {
    return this;
  }
}

export class LoginUserManageable extends LoginUserInput {
  constructor(obj?: any) {
    super();

    if (obj) {
      obj && Object.assign(
        this,
        LoginUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        LoginUserManageable.default()
      );
    }
  }

  static required(): any {
    return {
      email: true,
      password: true,
    };
  }

  static default(): any {
    return { ...new LoginUserInput() };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      LoginUserManageable.required(),
      this,
      LoginUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    return errors;
  }

  serialize(): any {
    return this;
  }
}

export class RegisterUserInputExtended extends RegisterUserInput {
  accountType?: AccountType;
  confirmPassword?: string;
}

export class RegisterUserManageable extends RegisterUserInputExtended {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        RegisterUserManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        RegisterUserManageable.default()
      );
    }
  }

  required(): any {
    return {
      company: this.accountType === AccountType.Company ? {
        legalEntityType: true,
        registrationNumber: true,
        tradeRegister: true,
        name: true,
      } : false,
      confirmPassword: true,
      customerInfoEffectaGmbh: true,
      email: true,
      password: true,
      firstName: true,
      lastName: true,
      privacyPolicy: true,
      termsEffectaGmbh: true,
      termsFinproAG: true,
    };
  }

  static default(): any {
    return {
      ...new RegisterUserInput(),
      accountType: AccountType.Private,
      subscribedToProductUpdates: false,
      confirmPassword: "",
      company: new RegisterCompanyInput(),
      customerInfoEffectaGmbh: false,
      privacyPolicy: false,
      termsEffectaGmbh: false,
      termsFinproAG: false,
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      RegisterUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    errors = Object.assign(
      errors,
      validateBooleansTrue(new CustomValidationArguments({
        keys: ['customerInfoEffectaGmbh', 'privacyPolicy', 'termsEffectaGmbh', 'termsFinproAG'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    if (!errors.password && this.password !== this.confirmPassword) {
      errors.confirmPassword = messages[ValidationMessages.NOTMATCHING];
    }

    if (!errors.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(this.email)) {
      errors.email = messages[ValidationMessages.INVALID];
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (data.accountType === AccountType.Private) {
      delete data.company;
    } else {
      data.company.legalEntityType = data.company.legalEntityType.toUpperCase();
    }

    delete data.accountType;
    delete data.confirmPassword;

    return data;
  }
}

export class StartIdNowUserManageable extends StartIdNowUserInput {
  constructor(obj?: any) {
    super();

    if (obj) {
      obj && Object.assign(
        this,
        StartIdNowUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        StartIdNowUserManageable.default()
      );
    }
  }

  static required(): any {
    return { skipIdNow: true };
  }

  static default(): any {
    return {
      ...new StartIdNowUserInput(),
      skipIdNow: false,
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      StartIdNowUserManageable.required(),
      this,
      StartIdNowUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (process.env.REACT_APP_ENV === 'prod') {
      delete data.skipIdNow;
    }

    return data;
  }
}

export class SignDocumentUserManageable extends SignDocumentUserInput {
  constructor(obj?: any) {
    super();

    const defaultData = SignDocumentUserManageable.default();

    if (obj) {
      obj && Object.assign(
        this,
        defaultData,
        prune(extract(
          obj,
          Object.keys(this)
        )),
        { document: obj.document ? obj.document.toLowerCase() : defaultData.document }
      );
    } else {
      Object.assign(
        this,
        defaultData
      );
    }
  }

  static required(): any {
    return { document: true };
  }

  static default(): any {
    return {
      ...new SignDocumentUserInput(),
      document: '',
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      SignDocumentUserManageable.required(),
      this,
      SignDocumentUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    data.document = data.document.toUpperCase();

    return data;
  }
}

export class UpdateCompanyManageable extends UpdateCompanyInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        UpdateCompanyManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        UpdateCompanyManageable.default()
      );
    }
  }

  required(): any {
    return {
      representativeFirstName: true,
      representativeLastName: true,
    };
  }

  static default(): any {
    return {
      ...new UpdateCompanyInput(),
      representativeFirstName: "",
      representativeLastName: "",
    }
  }

  serialize(
    keys: Array<string>, initialValues: any
  ): any {
    const shouldProcess = (key: string): boolean => {
      return keys.includes(key);
    };

    const cutIrrelevant = (input: any) => Object.entries(input).reduce(
      (
        result: any, [key, value]
      ) => {
        if (shouldProcess(key) && JSON.stringify(value) !== JSON.stringify(initialValues[key])) {
          result[key] = value;
        }
        return result;
      },
      {}
    )

    let data = cutIrrelevant(this);

    return data;
  }
}

export class UpdateUserInputExtended extends UpdateUserInput {
  notProvidingExperience?: boolean;
  password?: string;
  walletPassword?: string;
  confirmWalletPassword?: string;
}

export class UpdateUserManageable extends UpdateUserInputExtended {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        UpdateUserManageable.default(),
        prune(extract(
          obj,
          Object.keys(this)
        ))
      );
    } else {
      Object.assign(
        this,
        UpdateUserManageable.default()
      );
    }
  }

  required(): any {
    return {
      company: UserManageable.isCompany(this) ? new UpdateCompanyManageable(this.company).required() : false,
      firstName: true,
      lastName: true,
      cellphone: true,
      birthDate: true,
      birthCity: true,
      gender: true,
      citizenship: true,
      taxNumber: true,
      notUSTax: true,
      politicallyExposed: true,
      politicallyExposedReason: this.politicallyExposed,
      idDocumentExpiryDate: true,
      idDocumentIssueDate: true,
      idDocumentIssuingAgency: true,
      idDocumentType: true,
      idDocumentNumber: true,
      idDocumentCountry: true,
      address: true,
      addressCity: true,
      addressZipcode: true,
      addressCountry: true,
      employment: true,
      highestEducation: true,
      experience: true,
      password: true,
      walletPassword: true,
      confirmWalletPassword: true,
    };
  }

  static default(): any {
    return {
      ...new UpdateUserInput(),
      address: "",
      addressZipcode: "",
      addressCity: "",
      addressCountry: "",
      birthCity: "",
      birthDate: "",
      cellphone: "",
      citizenship: "",
      defaultPaymentMethod: "",
      employment: "",
      experience: {
        skills: [],
        activities: [],
      },
      firstName: "",
      gender: "",
      highestEducation: null,
      idDocumentCountry: "",
      idDocumentExpiryDate: "",
      idDocumentIssueDate: "",
      idDocumentIssuingAgency: "",
      idDocumentNumber: "",
      idDocumentType: "",
      notUSTax: false,
      politicallyExposed: "",
      politicallyExposedReason: undefined,
      taxNumber: "",
      company: UpdateCompanyManageable.default(),
      password: "",
      walletPassword: "",
      confirmWalletPassword: "",
    }
  }

  automatic(): any {
    const notProvidingExperience = randomBoolean();

    const today = new Date();

    let tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    let minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 1);

    let maxDate = new Date();
    maxDate.setFullYear(today.getFullYear() + 1);

    let politicallyExposed = randomSelectOption('yesNo');

    return {
      ...UpdateUserManageable.default(),
      firstName: this.firstName,
      lastName: this.lastName,
      cellphone: "+49232323543",
      birthDate: randomDate(),
      birthCity: "X",
      gender: randomSelectOption('gender'),
      citizenship: randomAutocompleteOption('countries'),
      taxNumber: "X",
      notUSTax: true,
      politicallyExposed,
      politicallyExposedReason: politicallyExposed ? randomAdjective() + " " + randomNoun() : undefined,
      idDocumentExpiryDate: dayjs(randomDate(
        tomorrow,
        maxDate
      )).toISOString(),
      idDocumentIssueDate: dayjs(randomDate(
        minDate,
        today
      )).toISOString(),
      idDocumentIssuingAgency: "X",
      idDocumentType: randomSelectOption('idDocumentType'),
      idDocumentNumber: randomString(6),
      idDocumentCountry: randomAutocompleteOption('countries'),
      address: "X",
      addressCity: "X",
      addressZipcode: "X",
      addressCountry: randomAutocompleteOption('countries'),
      employment: "X",
      highestEducation: randomAutocompleteOption('educationLevel'),
      notProvidingExperience: notProvidingExperience,
      experience: notProvidingExperience ? null : {
        skills: Object.values(InvestmentClass).map((type: any) => {
          const randomLevel = randomSelectOption('experience');

          return Object.assign(
            new Experience(),
            {
              hasKnowledge: randomBoolean(),
              type,
              level: randomLevel,
            }
          )
        }),
        activities: Object.values(ActivityType).map((type: any) => {
          const randomLevelWithoutAdvice = randomSelectOption('experience');
          const randomLevellevelWithAdvice = randomSelectOption('experience');
          const randomLevelWithAssetManagement = randomSelectOption('experience');
          const randomTransactionsPerYear = randomSelectOption('transactionsPerYear');
          const randomVolumePerYear = randomSelectOption('volumePerYear');

          return Object.assign(
            new Experience(),
            {
              type,
              levelWithoutAdvice: randomLevelWithoutAdvice,
              levelWithAdvice: randomLevellevelWithAdvice,
              levelWithAssetManagement: randomLevelWithAssetManagement,
              transactionsPerYear: randomTransactionsPerYear,
              volumePerYear: randomVolumePerYear,
            }
          )
        }),
      },
      company: UserManageable.isCompany(this) ? {
        ...this.company,
        representativeFirstName: randomAdjective(),
        representativeLastName: randomNoun(),
      } : null,
    };
  }

  hasActivityExperience(type: ActivityType) {
    return UserManageable.hasActivityExperience(
      this,
      type
    );
  }

  isExperienceProvided() {
    return UserManageable.isExperienceProvided(this);
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    errors = Object.assign(
      errors,
      validateCellphones(new CustomValidationArguments({
        keys: ['cellphone'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.INVALID]
      }))
    );

    errors = Object.assign(
      errors,
      validateBooleansTrue(new CustomValidationArguments({
        keys: ['notUSTax'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    errors = Object.assign(
      errors,
      validateDates(new CustomValidationArguments({
        keys: ['birthDate', 'idDocumentExpiryDate', 'idDocumentIssueDate'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      UpdateUserManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    if (!errors.birthDate && shouldValidate("birthDate")) {
      let maxBirthDate = new Date();
      maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 18);
      if (dayjs(this.birthDate).isAfter(maxBirthDate)) {
        errors.birthDate = messages[ValidationMessages.INVALID];
      }
    }

    if (!errors.idDocumentExpiryDate && shouldValidate("idDocumentExpiryDate")) {
      const today = new Date();
      if (!dayjs(this.idDocumentExpiryDate).isAfter(today)) {
        errors.idDocumentExpiryDate = messages[ValidationMessages.INVALID];
      }
    }

    if (!errors.idDocumentIssueDate && shouldValidate("idDocumentIssueDate")) {
      const today = new Date();
      if (dayjs(this.idDocumentIssueDate).isAfter(today)) {
        errors.idDocumentIssueDate = messages[ValidationMessages.INVALID];
      }
    }

    if (this.experience && this.experience.activities && shouldValidate("experience")) {
      for (let i = 0; i < this.experience.activities.length; i++) {
        const activitiesErrors = validateNumbersInRange(
          [{
            key: 'transactionsPerYear',
            min: 0
          }, {
            key: 'volumePerYear',
            min: 0
          }],
          {},
          this.experience.activities[i],
          keys.includes('experience') ? ['transactionsPerYear', 'volumePerYear'] : [],
          messages[ValidationMessages.MINIMUM],
          messages[ValidationMessages.MAXIMUM]
        );

        if (!isEmptyObject(activitiesErrors)) {
          if (errors.experience === undefined) {
            errors.experience = {};
          }

          if (errors.experience.activities === undefined) {
            errors.experience.activities = [];
          }

          errors.experience.activities[i] = activitiesErrors;
        }
      }
    }

    if (!errors.walletPassword && this.walletPassword !== this.confirmWalletPassword && shouldValidate("confirmWalletPassword")) {
      errors.confirmWalletPassword = messages[ValidationMessages.NOTMATCHING];
    }

    return errors;
  }

  serialize(
    keys: Array<string>, initialValues: UserManageable
  ): any {
    const shouldProcess = (key: string): boolean => {
      return keys.includes(key);
    };

    const cutIrrelevant = (input: any) => Object.entries(input).reduce(
      (
        result: any, [key, value]
      ) => {
        if (shouldProcess(key) && JSON.stringify(value) !== JSON.stringify(initialValues[key])) {
          result[key] = value;
        }
        return result;
      },
      {}
    )

    let data = cutIrrelevant(this);

    if (data.birthDate && dayjs(data.birthDate).isValid()) {
      data.birthDate = dayjs(data.birthDate).toISOString();
    }

    if (data.idDocumentExpiryDate && dayjs(data.idDocumentExpiryDate).isValid()) {
      data.idDocumentExpiryDate = dayjs(data.idDocumentExpiryDate).toISOString();
    }

    if (data.idDocumentIssueDate && dayjs(data.idDocumentIssueDate).isValid()) {
      data.idDocumentIssueDate = dayjs(data.idDocumentIssueDate).toISOString();
    }

    if (data.highestEducation) {
      data.highestEducation = data.highestEducation.toUpperCase();
    }

    if (data.gender) {
      data.gender = data.gender.toUpperCase();
    }

    if (data.idDocumentType) {
      data.idDocumentType = data.idDocumentType.toUpperCase();
    }

    if (data.experience) {
      data.experience = {
        skills: data.experience.skills ? data.experience.skills.map((skill: Skill) => ({
          ...skill,
          type: skill.type.toUpperCase(),
          level: skill.level.toUpperCase(),
        })) : [],
        activities: data.experience.activities ? data.experience.activities.map((activity: Activity) => ({
          ...activity,
          type: activity.type.toUpperCase(),
          levelWithoutAdvice: activity.levelWithoutAdvice.toUpperCase(),
          levelWithAdvice: activity.levelWithAdvice.toUpperCase(),
          levelWithAssetManagement: activity.levelWithAssetManagement.toUpperCase(),
          transactionsPerYear: activity.transactionsPerYear ? activity.transactionsPerYear.toUpperCase() : undefined,
          volumePerYear: activity.volumePerYear ? activity.volumePerYear.toUpperCase() : undefined,
        })) : [],
      };
    }

    if (!UserManageable.isCompany(initialValues)) {
      delete data.company;
    } else {
      data.company = new UpdateCompanyManageable(data.company).serialize(
        keys,
        initialValues.company
      );
    }

    delete data.notProvidingExperience;
    delete data.password;
    delete data.walletPassword;
    delete data.confirmWalletPassword;

    return data;
  }
}

export type UserType =
  | ApproveUserInput
  | LoginUserInput
  | RegisterUserInputExtended
  | SignDocumentUserInput
  | StartIdNowUserInput
  | UpdateUserInputExtended
  | User

export type UserInputTypeUnion =
  & ApproveUserInput
  & LoginUserInput
  & RegisterUserInputExtended
  & SignDocumentUserInput
  & StartIdNowUserInput
  & UpdateUserInputExtended

export type CompanyType =
  | RegisterCompanyInput
  | UpdateCompanyInput
  | Company

export type CompanyInputTypeUnion =
  & RegisterCompanyInput
  & UpdateCompanyInput

export type ExperienceType =
  | UpdateExperienceInput
  | Experience

export type ExperienceInputTypeUnion =
  & UpdateExperienceInput

export type ActivityTypeIntersection =
  | UpdateActivityInput
  | Activity

export type ActivityInputTypeUnion =
  & UpdateActivityInput

export type SkillTypeIntersection =
| UpdateSkillInput
| Skill

export type SkillInputTypeUnion =
& UpdateSkillInput